import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { DateParsersService } from 'rev-shared/date/DateParsers.Service';
import { MinuteMs, MinutesPerHour } from 'rev-shared/date/Time.Constant';
import { formatTimespanShort } from 'rev-shared/date/DateFormatters';
import { lastValueFrom } from 'rev-shared/rxjs/lastValueFrom';

import { ACCOUNT_DETAIL_SERVICE_TOKEN, AccountModel } from './AccountDetail.Provider';
import { IAutoIngestHours } from './IAutoIngestHours';
import { UserContextService } from './UserContext.Service';

import { FeatureName } from './FeatureName';

interface IAccountDetailResponse {
	aiCreditsBuckets: any[];
	viewingHoursBuckets: any[];
}

@Injectable({
	providedIn: 'root'
})
export class UserAccountService {
	public currentAdminAccount: AccountModel = {} as AccountModel;
	public fullAccountDetails: { [key: string]: AccountModel } = {};
	public readonly RouteScope = 'Admin.Accounts';
	constructor(
		@Inject(ACCOUNT_DETAIL_SERVICE_TOKEN) public AccountDetail: any,
		private readonly DateParsers: DateParsersService,
		private readonly UserContext: UserContextService,
		private readonly http: HttpClient
	) {}

	/**
	 * Return the user's account ID or the admin account ID if one is currently being administered
	 */
	public get workingAccountId() {
		return this.currentAdminAccount.id || this.UserContext.getAccount().id;
	}

	/**
		Set currentAdminAccount using an existing account in the hash
	**/
	public enterAccountAdmin(accountId: string): void {
		const accountDetail = this.fullAccountDetails[accountId];
		if (!accountDetail) {
			throw new Error('AccountDetail for ' + accountId + ' must be loaded');
		}

		this.currentAdminAccount = this.fullAccountDetails[accountId];
	}

	/**
		Clear currentAdminAccount
	**/
	public exitAccountAdmin(): void {
		this.currentAdminAccount = {} as AccountModel;
	}

	/**
		Retrieve an account by ID from the server and push it into the accounts hash.
	**/
	public fetchAccountDetail(accountId: string): Promise<any> {
		return lastValueFrom(this.http.get<IAccountDetailResponse>(`/network/accounts/${accountId}`))
			.then(account => this.loadAccount(account));
	}

	public fetchAdminAccountDetail(accountId: string): Promise<any> {
		return lastValueFrom(this.http.get<IAccountDetailResponse>(`/network/accounts/${accountId}/admin`))
			.then(account => this.loadAccount(account));
	}

	private loadAccount(account: any): any {
		const data = Object.assign(account, { ...account.licenses });
		delete data.licenses;

		this.readLicenseBuckets(data.viewingHoursBuckets);
		this.readLicenseBuckets(data.distributionHoursBuckets);
		this.readLicenseBuckets(data.aiCreditsBuckets, data.aiCreditsFeature);
		this.pushAccountDetail(data);

		return data;
	}

	public fetchParentAutoIngestHours(accountId: string): Promise<IAutoIngestHours> {
		return lastValueFrom(this.http.get(`/network/accounts/${accountId}/parent-auto-ingest-hours`));
	}

	private readLicenseBuckets(buckets: any[], aiCreditsFeature: string = null): void {
		if (!buckets) {
			return;
		}

		const isAiCreditsFeature = aiCreditsFeature === FeatureName.AI_MICROCREDITS;
		const unitConversion = isAiCreditsFeature ? 1 : MinutesPerHour;

		buckets.forEach(bucket =>
		{
			bucket.overage = bucket.used > bucket.allocated ? (bucket.used - bucket.allocated) / unitConversion : 0;
			bucket.allocated = bucket.allocated / unitConversion;
			bucket.used = isAiCreditsFeature ? bucket.used : formatTimespanShort(bucket.used * MinuteMs, true);
			bucket.expiresOn = this.DateParsers.parseUTCDate(bucket.expiresOn);
		});
	}

	/**
		Set or update the account in the hash
	**/
	public pushAccountDetail(cfg: any): void {
		const data = {
			id: cfg.id || cfg.accountId,
			...cfg,
		};
		delete data.accountId;
		const account = this.fullAccountDetails[data.id];
		if (account && account.update) {
			account.update(data);
		} else {
			this.fullAccountDetails[data.id] = new this.AccountDetail(data);
		}

		if (this.fullAccountDetails[data.parentAccountId]) {
			this.pushChildAccount(data);
		}
	}


	/**
		Set or update the child account in the parent account's children array
	**/
	public pushChildAccount(cfg: any): void {
		const data = {
			id: cfg.id || cfg.accountId,
			...cfg
		};
		delete data.accountId;

		const account = this.fullAccountDetails[data.parentAccountId];
		if(account){
			account.pushChildAccount(data);
		}
	}
}
